import { IContactCompany } from 'models/contact_companies'
import { CompanyFormShape, CompanyModal } from './CompanyModal'
import { useMemo } from 'react'
import useApiService from 'services/api'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../UI/Modal/Modal'
import { Spinner } from 'react-bootstrap'

export function EditCompanyModal({
  show,
  setShow,
  company,
  loading = false,
  error = false,
  skipDealCreation = false,
}: {
  show: boolean
  loading?: boolean
  error?: boolean
  skipDealCreation?: boolean
  setShow: (show: boolean) => void
  company?: IContactCompany | null
}) {
  const api = useApiService()
  const queryClient = useQueryClient()

  const apiAction = async (values: CompanyFormShape) => {
    if (!company) return
    delete values.taglist
    delete values.distributors
    const payload = {
      ...values,
      name: values.name?.trim() || '',
      country: values.country || 'US',
      state: values.state || '',
      city: values.city || '',
      domain: values.domain?.toLowerCase() || '',
      address: values.address || '',
      zipcode: values.zipcode || '',
    }
    const response = await api
      .updateCompany(company.id, payload)
      .then((response) => {
        toast.success('Company updated successfully')
        return response
      })
    return response
  }

  const { mutateAsync } = useMutation({
    mutationFn: apiAction,
    onMutate: async (values: CompanyFormShape) => {
      if (!company) return
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['company', company.id],
      })

      // Snapshot the previous value
      const previousCompany = queryClient.getQueryData(['company', company.id])

      // Optimistically update the cache
      queryClient.setQueryData(
        ['company', company.id],
        (old: IContactCompany) => {
          if (!old) return old
          return {
            ...old,
            name: values.name?.trim() || old.name,
            domain: values.domain?.toLowerCase() || old.domain,
            country: values.country || old.country,
            state: values.state || old.state,
            city: values.city || old.city,
            address: values.address || old.address,
            zipcode: values.zipcode || old.zipcode,
            phone_number: values.phone_number || old.phone_number,
            extension: values.extension || old.extension,
          }
        }
      )

      // Return context for rollback
      return { previousCompany }
    },
    onError: (err, values, context) => {
      if (!company) return
      // Rollback on error
      if (context?.previousCompany) {
        queryClient.setQueryData(
          ['company', company.id],
          context.previousCompany
        )
      }
      toast.error('Failed to update company')
    },
    onSettled: () => {
      if (!company) return
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['company', company.id],
      })
    },
  })

  const optimisticApiAction = async (values: CompanyFormShape) => {
    return await mutateAsync(values)
  }

  const defaultValues: CompanyFormShape | undefined = useMemo(() => {
    if (!company) return undefined
    return {
      ...company,
      category: company.category_id,
      sub_category: company.sub_category_id,
      distributors: company.distributors?.map((d) => d.id),
      taglist: company.taglist?.map((tag) => tag.id),
      unique_domain: true,
    }
  }, [company])

  if (error && !loading && !company) {
    return (
      <Modal
        open={show}
        onOpenChange={(isOpen) => setShow(isOpen)}
        title="Edit Company"
        description="Failed to load company data."
        size="lg"
        onAccept={() => setShow(false)}
        acceptButtonText="Close"
      >
        <p className="text-center text-muted py-12">
          Something went wrong while loading the company. Please try again.
        </p>
      </Modal>
    )
  }

  if (loading || !company) {
    return (
      <Modal
        open={show}
        onOpenChange={(isOpen) => setShow(isOpen)}
        title="Edit Company"
        description="Loading company data..."
        size="lg"
      >
        <div className="flex items-center justify-center py-12">
          <Spinner animation="border" />
        </div>
      </Modal>
    )
  }

  return (
    <CompanyModal
      show={show}
      setShow={setShow}
      hideKnownDistributorsAndTags
      defaultValues={defaultValues}
      skipDealCreation={skipDealCreation}
      apiAction={optimisticApiAction}
      title="Edit Company"
      description="Add or modify information about this company."
      acceptButtonText="Apply Changes"
    />
  )
}
